import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  VStack,
} from '@chakra-ui/react'
import { Field, FieldProps, Formik } from 'formik'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiOutlineBookmark } from 'react-icons/hi2'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import { colors } from '../../../ui-kit'
import useToast from '../../hooks/useToast'
import {
  CreateSavedSearchSuccessResponse,
  SavedSearchErrorResponse,
} from '../../interfaces'
import { UserContext } from '../../providers'

interface Props {
  isOpen: boolean
  onClose(): void
  url: string // The exact URL to save
  onSaveSuccess?(): void // Optional callback for success
}

interface FormProps {
  name: string
  isPrivate: boolean
}

const SaveSearchModalNew = ({ isOpen, onClose, url, onSaveSuccess }: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [formError, setFormError] = useState<string>('')
  const { user } = useContext(UserContext)
  const toast = useToast()
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
        setFormError('')
      }}
      size="xl"
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <Formik<FormProps>
          initialValues={{
            name: '',
            isPrivate: true,
          }}
          validationSchema={Yup.object().shape({
            name: Yup.string().required('Required'),
            isPrivate: Yup.boolean().required('Required'),
          })}
          onSubmit={async values => {
            setFormError('')
            const res = (await request.post(
              `/api/media-library/${user?.clientNumber}/saved-searches`,
              { ...values, url },
            )) as CreateSavedSearchSuccessResponse | SavedSearchErrorResponse

            if (request.error) {
              setFormError(
                (res as SavedSearchErrorResponse).message ??
                  t(
                    'error_in_saving_search',
                    'Error in saving search, please try again later.',
                  ),
              )
            } else {
              toast({
                description: t(
                  'search_saved_successfully',
                  'Search saved successfully.',
                ),
                status: 'success',
                isClosable: true,
                duration: 5000,
                position: 'top-right',
              })
              setFormError('')
              onClose()
              onSaveSuccess?.() // Call success callback if provided
            }
          }}
        >
          {({ submitForm, errors, touched, isSubmitting, dirty, isValid }) => (
            <>
              <ModalHeader>
                <Icon
                  as={HiOutlineBookmark}
                  color="appBattleshipGrey"
                  backgroundColor="appPaleGrey"
                  boxSize={6}
                  padding="0.1em"
                  borderRadius="full"
                  marginRight="0.5em"
                />
                {t('save_search', 'Save search')}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {formError ? (
                  <Alert status="error" mb="14px">
                    <AlertIcon />
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                ) : null}
                <Field name="name">
                  {({ field }: FieldProps<string, FormProps>) => (
                    <FormControl
                      isRequired
                      isInvalid={Boolean(errors.name && touched.name)}
                      mb={4}
                    >
                      <FormLabel>{t('name', 'Name')}</FormLabel>
                      <Input
                        {...field}
                        placeholder={t(
                          'saved_search_name_placeholder',
                          'Enter saved search name',
                        )}
                      />
                      {errors.name && touched.name ? (
                        <FormErrorMessage>{errors.name}</FormErrorMessage>
                      ) : null}
                    </FormControl>
                  )}
                </Field>
                <Field name="isPrivate">
                  {({ field, form }: FieldProps<boolean, FormProps>) => (
                    <FormControl
                      isRequired
                      isInvalid={Boolean(
                        form.errors.isPrivate && form.touched.isPrivate,
                      )}
                      mb={4}
                    >
                      <FormLabel>{t('visibility', 'Visibility')}</FormLabel>
                      <RadioGroup
                        value={field.value ? 'true' : 'false'}
                        onChange={(value: string) => {
                          form.setFieldValue(field.name, value === 'true')
                        }}
                      >
                        <VStack align="start">
                          <Radio value="true">
                            {t('only_to_me', 'Only to me')}
                          </Radio>
                          <Radio value="false">
                            {t('shared_with_everyone', 'Shared with everyone')}
                          </Radio>
                        </VStack>
                      </RadioGroup>
                      {form.errors.isPrivate && form.touched.isPrivate ? (
                        <FormErrorMessage>
                          {form.errors.isPrivate}
                        </FormErrorMessage>
                      ) : null}
                    </FormControl>
                  )}
                </Field>
              </ModalBody>
              <ModalFooter
                borderTop={`1px solid ${colors.appGrey}`}
                style={{ gap: '1em' }}
              >
                <Button variant="outline" onClick={onClose}>
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  colorScheme="appBlue"
                  onClick={async () => await submitForm()}
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting || !dirty || !isValid}
                >
                  {t('submit', 'Submit')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default SaveSearchModalNew
